
import { 
    doc, 
    getDoc, 
    setDoc, 
    collection, 
    addDoc,
    onSnapshot,
    DocumentData,
    FirestoreError,
    updateDoc,
    deleteDoc,
    getDocs,
    query,
    where,
    orderBy,
    limit
} from "firebase/firestore";
import { db, functions } from "../firebase";
import { UserProfile, Request, SolutionRequest, Scenario, GrowthPlan, Company, SalesPlay, AcademyScenario, SimulatorType, AcademyCourse, UserCourseProgress, CourseEditRecord, CourseBlockComment } from "../types";
import { buildCreatorCourseAccess } from "../utils/courseAccess";
import { httpsCallable } from 'firebase/functions';

// --- Helper for Lazy Name Migration ---
const parseNameFromDisplayName = (displayName: string | undefined, email: string): { firstName: string, lastName: string } => {
    if (!displayName) {
        return { firstName: email?.split('@')[0] || '', lastName: '' };
    }

    // Check for "LastName, FirstName" format (common in SSO like EntraID)
    if (displayName.includes(',')) {
        const parts = displayName.split(',');
        return {
            lastName: parts[0].trim(),
            firstName: parts.slice(1).join(' ').trim()
        };
    }

    // Fallback to "FirstName LastName" format
    const parts = displayName.split(' ');
    if (parts.length > 1) {
        return {
            firstName: parts[0].trim(),
            lastName: parts.slice(1).join(' ').trim()
        };
    }

    return { firstName: displayName.trim(), lastName: '' };
};

// --- User & Company Management ---

export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
        let profile = { id: userSnap.id, ...userSnap.data() } as UserProfile;
        
        // Lazy Migration: Fix missing first/last names for existing users
        if (!profile.firstName || !profile.lastName) {
            // Need to cast to any to access displayName which isn't in UserProfile type but exists in old docs
            const oldData = userSnap.data() as any; 
            const parsedName = parseNameFromDisplayName(oldData.displayName as string | undefined, profile.email);
            
            // Only update if we successfully parsed something new to avoid infinite loops
            if ((parsedName.firstName && !profile.firstName) || (parsedName.lastName && !profile.lastName)) {
                profile = {
                    ...profile,
                    firstName: profile.firstName || parsedName.firstName,
                    lastName: profile.lastName || parsedName.lastName,
                };
                
                try {
                    // Quietly update the database with the fixed names
                    await updateDoc(userRef, {
                        firstName: profile.firstName,
                        lastName: profile.lastName
                    });
                    console.log(`Lazy migration applied: Fixed name for user ${userId}`);
                } catch (e) {
                    console.warn(`Could not save lazy migration for user ${userId}:`, e);
                }
            }
        }
        
        return profile;
    }
    return null;
};

// Unified function for creating and updating user profiles
export const saveUserProfile = async (userId: string, data: Partial<UserProfile>): Promise<void> => {
    const userRef = doc(db, "users", userId);
    await setDoc(userRef, data, { merge: true });
};

export const updateUserProfile = async (userId: string, data: Partial<UserProfile>): Promise<void> => {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, data);
};

export const getUsers = async (): Promise<UserProfile[]> => {
    const usersCollection = collection(db, 'users');
    const userSnapshot = await getDocs(usersCollection);
    return userSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as UserProfile));
};


export const getCompanies = async (): Promise<Company[]> => {
    const companiesCollection = collection(db, 'companies');
    const companySnapshot = await getDocs(companiesCollection);
    return companySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Company));
};

export const addCompanyProfile = async (companyData: Partial<Company>): Promise<string> => {
    const companiesCollection = collection(db, 'companies');
    const docRef = await addDoc(companiesCollection, companyData);
    return docRef.id;
}


// --- Solution Navigator ---

export const requestSolutionPlay = async (requestData: Partial<SolutionRequest>): Promise<string> => {
    const requestsCollection = collection(db, "solutionRequests");
    const docRef = await addDoc(requestsCollection, {
        ...requestData,
        status: 'queued',
        createdAt: new Date(),
    });
    return docRef.id;
};

export const listenToSolutionRequest = (
    requestId: string,
    callback: (data: SolutionRequest | null) => void
): (() => void) => { 
    const docRef = doc(db, "solutionRequests", requestId);
    const unsubscribe = onSnapshot(docRef, 
        (docSnap: DocumentData) => {
            if (docSnap.exists()) {
                callback({ id: docSnap.id, ...docSnap.data() } as SolutionRequest);
            } else {
                callback(null);
            }
        },
        (error: FirestoreError) => {
            console.error("Error listening to solution request:", error);
            callback(null);
        }
    );
    return unsubscribe;
};


// --- Growth Planner ---

export const requestGrowthPlan = async (partnerProfile: any): Promise<GrowthPlan> => {
    const generateGrowthPlanFn = httpsCallable(functions, 'generateGrowthPlan');
    const result = await generateGrowthPlanFn({ partnerProfile });
    return result.data as GrowthPlan;
};

export const saveGrowthPlan = async (userId: string, plan: GrowthPlan): Promise<string> => {
    const plansCollection = collection(db, "growthPlans");
    const docRef = await addDoc(plansCollection, {
      userId,
      ...plan,
      createdAt: new Date(),
    });
    return docRef.id;
  };

// --- Sales Assistant ---

export const askSalesAssistant = async (question: string, salesPlay: SalesPlay): Promise<string> => {
    const askAssistantFn = httpsCallable(functions, 'askSalesAssistant');
    const result = await askAssistantFn({ question, salesPlay });
    return result.data as string;
};


// --- Scenario Management ---

export const getScenarios = async (): Promise<Scenario[]> => {
    const scenariosCollection = collection(db, 'scenarios');
    try {
        const scenarioSnapshot = await getDocs(scenariosCollection);
        if (scenarioSnapshot.empty) {
            console.warn("Firestore query successful, but the 'scenarios' collection is empty.");
            return [];
        }
        return scenarioSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Scenario));
    } catch (error) {
        console.error("Firestore Error: Failed to get scenarios. This is likely a permissions issue.", error);
        throw error;
    }
};

export const getAcademyScenariosByType = async (simulatorType: SimulatorType): Promise<AcademyScenario[]> => {
    const scenariosCollection = collection(db, 'academyScenarios');
    const q = query(scenariosCollection, where("simulatorType", "==", simulatorType));
    try {
        const scenarioSnapshot = await getDocs(q);
        if (scenarioSnapshot.empty) {
            console.warn(`Firestore query successful, but no scenarios found for type: ${simulatorType}`);
            return [];
        }
        return scenarioSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as AcademyScenario));
    } catch (error) {
        console.error(`Firestore Error: Failed to get scenarios for type ${simulatorType}.`, error);
        throw error;
    }
};

export const saveScenario = async (scenarioData: Partial<Scenario>): Promise<string> => {
    if (scenarioData.id) {
        // Update existing scenario
        const scenarioRef = doc(db, 'scenarios', scenarioData.id);
        await setDoc(scenarioRef, scenarioData, { merge: true });
        return scenarioData.id;
    } else {
        // Create new scenario
        const scenariosCollection = collection(db, 'scenarios');
        const docRef = await addDoc(scenariosCollection, scenarioData);
        return docRef.id;
    }
};

export const updateScenario = async (scenarioId: string, data: Partial<Scenario>): Promise<void> => {
    const scenarioRef = doc(db, 'scenarios', scenarioId);
    await updateDoc(scenarioRef, data);
};


export const deleteScenario = async(id: string): Promise<void> => {
    const scenarioRef = doc(db, 'scenarios', id);
    await deleteDoc(scenarioRef);
}

// --- Academy Course Management ---

const mapAcademyCourseDocs = (snapshot: { docs: { id: string; data: () => DocumentData }[] }): AcademyCourse[] =>
    snapshot.docs.map(courseDoc => ({ ...courseDoc.data(), id: courseDoc.id } as AcademyCourse));

/** Learner catalog: published courses only. */
export const getPublishedAcademyCourses = async (): Promise<AcademyCourse[]> => {
    const coursesCollection = collection(db, 'academy_courses');
    try {
        const snapshot = await getDocs(query(coursesCollection, where('status', '==', 'Published')));
        return mapAcademyCourseDocs(snapshot);
    } catch (error) {
        console.error("Error getting published academy courses:", error);
        return [];
    }
};

/**
 * @deprecated Use getPublishedAcademyCourses for the learner catalog
 * or getAcademyCoursesForManagement for Studio.
 */
export const getAcademyCourses = async (): Promise<AcademyCourse[]> => {
    return getPublishedAcademyCourses();
};

export const getAcademyCoursesForManagement = async (
    uid: string,
    options?: { canSeeAll?: boolean }
): Promise<AcademyCourse[]> => {
    const coursesCollection = collection(db, 'academy_courses');
    try {
        if (options?.canSeeAll) {
            const snapshot = await getDocs(coursesCollection);
            return mapAcademyCourseDocs(snapshot);
        }

        const [byAccess, byCreator] = await Promise.all([
            getDocs(query(coursesCollection, where('accessUserIds', 'array-contains', uid))),
            getDocs(query(coursesCollection, where('createdBy', '==', uid))),
        ]);

        const byId = new Map<string, AcademyCourse>();
        [...mapAcademyCourseDocs(byAccess), ...mapAcademyCourseDocs(byCreator)].forEach(course => {
            if (course.id) byId.set(course.id, course);
        });
        return Array.from(byId.values());
    } catch (error) {
        console.error("Error getting academy courses for management:", error);
        return [];
    }
};

export const getAcademyCourseById = async (courseId: string): Promise<AcademyCourse | null> => {
    try {
        const snapshot = await getDoc(doc(db, 'academy_courses', courseId));
        if (!snapshot.exists()) return null;
        return { ...snapshot.data(), id: snapshot.id } as AcademyCourse;
    } catch (error) {
        console.error("Error getting academy course by id:", error);
        return null;
    }
};

export const getAcademyCourseBySlug = async (
    slug: string,
    options?: { uid?: string; canSeeAll?: boolean }
): Promise<AcademyCourse | null> => {
    const coursesCollection = collection(db, 'academy_courses');
    const fromDocs = (docs: { id: string; data: () => DocumentData }[]) => {
        const match = docs.find((courseDoc) => courseDoc.data().slug === slug);
        return match ? ({ ...match.data(), id: match.id } as AcademyCourse) : null;
    };

    try {
        // Course/super admins can query by slug alone. Everyone else must use
        // queries that match academy_courses list rules (published OR member ACL).
        if (options?.canSeeAll) {
            const snapshot = await getDocs(query(coursesCollection, where("slug", "==", slug)));
            if (snapshot.empty) return null;
            return { ...snapshot.docs[0].data(), id: snapshot.docs[0].id } as AcademyCourse;
        }

        const uid = options?.uid;
        const lookups: Promise<AcademyCourse | null>[] = [
            getDocs(query(coursesCollection, where('status', '==', 'Published'))).then((snapshot) => fromDocs(snapshot.docs)),
        ];
        if (uid) {
            lookups.push(
                getDocs(query(coursesCollection, where('accessUserIds', 'array-contains', uid))).then((snapshot) => fromDocs(snapshot.docs)),
                getDocs(query(coursesCollection, where('createdBy', '==', uid))).then((snapshot) => fromDocs(snapshot.docs))
            );
        }

        const results = await Promise.allSettled(lookups);
        for (const result of results) {
            if (result.status === 'fulfilled' && result.value) {
                return result.value;
            }
        }
        return null;
    } catch (error) {
        console.error("Error getting academy course by slug:", error);
        return null;
    }
};

export interface UserInfoParam {
    uid: string;
    displayName?: string;
    email?: string;
}

const sanitizeFirestorePayload = (obj: any): any => {
    if (obj === undefined) {
        return undefined;
    }
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }
    if (obj instanceof Date) {
        return obj;
    }
    if (Array.isArray(obj)) {
        return obj
            .filter((item) => item !== undefined)
            .map((item) => sanitizeFirestorePayload(item));
    }
    const cleaned: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
            const sanitizedValue = sanitizeFirestorePayload(value);
            if (sanitizedValue !== undefined) {
                cleaned[key] = sanitizedValue;
            }
        }
    }
    return cleaned;
};

export const saveAcademyCourse = async (
    courseData: Partial<AcademyCourse>,
    currentUser?: UserInfoParam,
    action: string = 'Updated course'
): Promise<string> => {
    const now = new Date();
    const nowIso = now.toISOString();

    const userName = currentUser?.displayName || currentUser?.email || 'Unknown User';
    const userEmail = currentUser?.email || '';

    const newLogRecord: CourseEditRecord | null = currentUser ? {
        userId: currentUser.uid,
        userName,
        userEmail,
        timestamp: nowIso,
        action
    } : null;

    const {
        members: _ignoredMembers,
        accessUserIds: _ignoredAccessUserIds,
        qualityRating: _ignoredQualityRating,
        id: _ignoredId,
        ...courseFields
    } = courseData;

    if (courseData.id) {
        const courseRef = doc(db, 'academy_courses', courseData.id);
        const existingHistory = courseData.editHistory || [];
        const updatedHistory = newLogRecord ? [newLogRecord, ...existingHistory] : existingHistory;

        // Ensure language is not modified on update: preserve existing stored language
        let preservedLanguage = courseData.language;
        try {
            const existingSnap = await getDoc(courseRef);
            if (existingSnap.exists()) {
                const existingData = existingSnap.data() as Partial<AcademyCourse>;
                if (existingData.language) {
                    preservedLanguage = existingData.language;
                }
            }
        } catch (e) {
            console.warn('Could not read existing course to verify language immutability:', e);
        }

        const updatePayload: any = sanitizeFirestorePayload({
            ...courseFields,
            ...(preservedLanguage ? { language: preservedLanguage } : {}),
            updatedAt: now,
            updatedBy: currentUser?.uid || courseData.updatedBy || '',
            updatedByName: userName !== 'Unknown User' ? userName : (courseData.updatedByName || ''),
            updatedByEmail: userEmail || courseData.updatedByEmail || '',
            editHistory: updatedHistory
        });

        await setDoc(courseRef, updatePayload, { merge: true });
        return courseData.id;
    } else {
        const coursesCollection = collection(db, 'academy_courses');
        const existingHistory = courseData.editHistory || [];
        const creationRecord: CourseEditRecord | null = currentUser ? {
            userId: currentUser.uid,
            userName,
            userEmail,
            timestamp: nowIso,
            action: action === 'Updated course' ? 'Created course' : action
        } : null;
        const updatedHistory = creationRecord ? [creationRecord, ...existingHistory] : existingHistory;

        const creatorUid = courseData.createdBy || currentUser?.uid || '';
        const acl = creatorUid
            ? buildCreatorCourseAccess(creatorUid, courseData.createdByName || (userName !== 'Unknown User' ? userName : ''), courseData.createdByEmail || userEmail, nowIso)
            : { members: {}, accessUserIds: [] as string[] };

        const autoSlug = courseFields.slug || (courseFields.title
            ? courseFields.title
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .trim()
                .replace(/[\s_]+/g, '-')
                .replace(/[^\w-]+/g, '')
                .replace(/--+/g, '-')
                .replace(/^-+|-+$/g, '') || (courseData.language ? `course-${courseData.language}` : 'course')
            : '');

        const createPayload: any = sanitizeFirestorePayload({
            ...courseFields,
            slug: autoSlug,
            language: courseData.language || 'en',
            createdAt: courseData.createdAt || now,
            createdBy: creatorUid,
            createdByName: courseData.createdByName || (userName !== 'Unknown User' ? userName : ''),
            createdByEmail: courseData.createdByEmail || userEmail,
            updatedAt: now,
            updatedBy: currentUser?.uid || courseData.updatedBy || '',
            updatedByName: userName !== 'Unknown User' ? userName : (courseData.updatedByName || ''),
            updatedByEmail: userEmail || courseData.updatedByEmail || '',
            editHistory: updatedHistory,
            members: acl.members,
            accessUserIds: acl.accessUserIds
        });

        const docRef = await addDoc(coursesCollection, createPayload);
        return docRef.id;
    }
};

export const deleteAcademyCourse = async (id: string): Promise<void> => {
    const courseRef = doc(db, 'academy_courses', id);
    await deleteDoc(courseRef);
};

// --- General Requests ---
export const submitRequest = async (request: Partial<Request>): Promise<string> => {
    const requestsCollection = collection(db, "requests");
    const docRef = await addDoc(requestsCollection, {
        ...request,
        status: 'pending',
        createdAt: new Date(),
    });
    return docRef.id;
};

export const firestoreService = {
    async getRecentContent(source: string, userId: string): Promise<SalesPlay[]> {
        const contentRef = collection(db, 'content');
        const q = query(
            contentRef, 
            where('source', '==', source), 
            where('createdBy', '==', userId),
            orderBy('createdAt', 'desc'),
            limit(20)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => doc.data() as SalesPlay);
    }
};

// --- ACADEMY COURSE PROGRESS ---

export const getUserCourseProgress = async (userId: string, courseId: string): Promise<UserCourseProgress | null> => {
    const progressRef = collection(db, 'user_course_progress');
    const q = query(
        progressRef,
        where('userId', '==', userId),
        where('courseId', '==', courseId),
        limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as UserCourseProgress;
};

export const getAllUserCourseProgress = async (userId: string): Promise<UserCourseProgress[]> => {
    const progressRef = collection(db, 'user_course_progress');
    const q = query(
        progressRef,
        where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as UserCourseProgress));
};

export const getRecentUserCourseProgress = async (
    userId: string,
    pageSize: number
): Promise<UserCourseProgress[]> => {
    const progressRef = collection(db, 'user_course_progress');
    const q = query(
        progressRef,
        where('userId', '==', userId),
        orderBy('lastVisitedAt', 'desc'),
        limit(pageSize)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as UserCourseProgress));
};

export const saveUserCourseProgress = async (userId: string, courseId: string, progressData: Partial<UserCourseProgress>): Promise<void> => {
    const progressRef = collection(db, 'user_course_progress');
    const q = query(
        progressRef,
        where('userId', '==', userId),
        where('courseId', '==', courseId),
        limit(1)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
        await addDoc(progressRef, {
            ...progressData,
            userId,
            courseId,
        });
    } else {
        const docRef = doc(db, 'user_course_progress', snapshot.docs[0].id);
        await updateDoc(docRef, progressData);
    }
};

// --- Course Review Comments ---
export const getCourseComments = async (courseId: string): Promise<CourseBlockComment[]> => {
    const commentsRef = collection(db, 'course_comments');
    const q = query(
        commentsRef,
        where('courseId', '==', courseId)
    );
    const snapshot = await getDocs(q);
    const comments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CourseBlockComment));
    // Sort in memory by createdAt ascending
    return comments.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
};

export const addCourseComment = async (commentData: Omit<CourseBlockComment, 'id'>): Promise<CourseBlockComment> => {
    const commentsRef = collection(db, 'course_comments');
    const docRef = await addDoc(commentsRef, {
        ...commentData,
        createdAt: commentData.createdAt || new Date().toISOString(),
        status: commentData.status || 'open'
    });
    return { id: docRef.id, ...commentData };
};

export const resolveCourseComment = async (
    commentId: string, 
    user?: { uid: string; displayName?: string }
): Promise<void> => {
    const docRef = doc(db, 'course_comments', commentId);
    await updateDoc(docRef, {
        status: 'resolved',
        resolvedAt: new Date().toISOString(),
        resolvedBy: user?.uid || 'unknown',
        resolvedByName: user?.displayName || 'Editor'
    });
};

export const deleteCourseComment = async (commentId: string): Promise<void> => {
    const docRef = doc(db, 'course_comments', commentId);
    await deleteDoc(docRef);
};


